/**
 * W-8BEN Models
 * Clean separation between API response model and form model with mapping logic
 */

// W8BEN Response Model - Fields that come from API endpoint
import { determineStateFieldType } from "../utils/formUtils";
export class W8BENResponseModel {
  // Basic fields typically from API
  "UniqueIdentifier": string = "";
  "Beneficial Owner name": string = "";
  "Date of birth": string = "";
  "Country of citizenship": string = "";
  "Permanent residence address": string = "";
  "Permanent city address": string = "";
  "Permanent address country": string = "";
  "Mailing address": string = "";
  "Mailing address city": string = "";
  "Mailing address country": string = "";
  "U.S. taxpayer identification number": string = "";
  "Foreign tax identifying number": string = "";
  "W8BEN-FTIN_not_required": boolean = false;
  "W8BEN-Reference_numbers": string = "";
  "W8BEN-Treaty_country": string = "";
  "W8BEN-Treaty_article": string = "";
  "W8BEN-Treaty_withholding_rate": string = "";
  "W8BEN-Treaty_income_type": string = "";
  "W8BEN-Treaty_conditions_explanation": string = "";
  "GIIN": string = "";
  "Signature": string = "";
  "Signature_date": string = "";
  "Signer_printed_name": string = "";
  "Signature_capacity": boolean = false;
  "Signature_unreadable": boolean = false;
  "isSigned": string = "";
  "isModified": string = "no";
  "FormName": string = "W-8BEN";

  // Dynamic fields support
  [key: string]: any;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }
}

// W8BEN Form Model - Complete form fields (more comprehensive)
export class W8BENFormModel {
  // Dynamic treaty fields support
  [key: string]: any;

  // Unique Identifier
  "UniqueIdentifier": string = "";

  // Form Information
  "Form_number": string = "";
  "Form_revision_date": string = "";
  "FormName": string = "W-8BEN";

  // Personal Information
  "Beneficial Owner name": string = "";
  "First name and middle name": string = "";
  "Last name": string = "";
  "Date of birth": string = "";
  "Country of citizenship": string = "";
  "Other": string = "";

  // Permanent Address
  "Permanent residence address": string = "";
  "Permanent address line 2": string = "";
  "Permanent address line 3": string = "";
  "Permanent address city": string = "";
  "Permanent address state USCA": string = "";
  "Permanent address state NonUS": string = "";
  "Permanent address zip": string = "";
  "Permanent address country": string = "";
  "Permanent address is care of": string = "no";
  "Permanent address is po box": string = "no";
  "Permanent address is rural route": string = "no";

  // Mailing Address
  "Mailing address": string = "";
  "Mailing address line 2": string = "";
  "Mailing address line 3": string = "";
  "Mailing city address": string = "";
  "Mailing address state USCA": string = "";
  "Mailing address state NonUS": string = "";
  "Mailing address zip": string = "";
  "Mailing address country": string = "";
  "Mailing address is care of": string = "no";
  "Mailing address is po box": string = "no";
  "Mailing address is rural route": string = "no";

  // Tax Information
  "Taxpayer email": string = "";
  "Taxpayer type": string = "";
  "U.S. taxpayer identification number": string = "";
  "Foreign tax identifying number": string = "";
  "W8BEN-FTIN_not_required": boolean = false;
  "W8BEN-Reference_numbers": string = "";

  // Treaty Information (base fields)
  "W8BEN-Treaty_country": string = "";
  "W8BEN-Treaty_article": string = "";
  "W8BEN-Treaty_withholding_rate": string = "";
  "W8BEN-Treaty_income_type": string = "";
  "W8BEN-Treaty_conditions_explanation": string = "";

  // Certification
  "Signature_capacity": boolean = false;
  "isSigned": string = "no";
  "isModified": string = "no";

  // Signature Information
  "Signer_printed_name": string = "";
  "Signature_date": string = "";
  "Signature_unreadable": boolean = false;

  "GIIN": string = "";
  "Signature": string = "";

  // Mapping control field
  "isMapDone": boolean = false;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Validate the form data
   */
  validate(): ValidationResult {
    const errors: string[] = [];
    const fieldErrors: Record<string, string> = {};

    // Required field validations - Updated per user requirements
    // Permanent address, Date of birth, and Country of citizenship removed as mandatory per user request
    const requiredFields = ["UniqueIdentifier", "First name and middle name", "Last name"];

    requiredFields.forEach((field) => {
      const value = (this as any)[field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        const fieldName = field.replace("W8BEN-", "").replace(/_/g, " ");
        const error = `${fieldName} is required`;
        errors.push(error);
        fieldErrors[field] = error;
      }
    });

    // Date validations
    if (this["Date of birth"] && !this.isValidDate(this["Date of birth"])) {
      const error = "Date of birth must be a valid date";
      errors.push(error);
      fieldErrors["Date of birth"] = error;
    }

    if (this["Signature_date"] && !this.isValidDate(this["Signature_date"])) {
      const error = "Signature date must be a valid date";
      errors.push(error);
      fieldErrors["Signature_date"] = error;
    }

    // Treaty benefits validation - Removed treaty article requirement per user request
    // Treaty article is no longer a required field

    // Certification validation - removed per user request

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors,
    };
  }

  /**
   * Check if a date string is valid
   */
  private isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  }

  /**
   * Convert to API payload format
   */
  toApiPayload(): Record<string, any> {
    return {
      ...this,
    };
  }
}

export class ValidationResult {
  isValid: boolean = false;
  errors: string[] = [];
  fieldErrors: Record<string, string> = {};
}

/**
 * W8BEN Field Mapper - Maps response model fields to form model fields
 */
export class W8BENFieldMapper {
  /**
   * Map response model to form model
   */
  static mapResponseToForm(responseModel: W8BENResponseModel): W8BENFormModel {
    const formModel = new W8BENFormModel();

    // Check if mapping is already done
    if (responseModel["isMapDone"]) {
      // If already mapped, just return the existing data as form model
      Object.assign(formModel, responseModel);
      return formModel;
    }

    // Direct field mappings (fields that exist in both models)
    const directMappings = [
      "UniqueIdentifier",
      "Beneficial Owner name",
      "Date of birth",
      "Country of citizenship",
      "Permanent address country",
      "Mailing address country",
      "U.S. taxpayer identification number",
      "Foreign tax identifying number",
      "W8BEN-FTIN_not_required",
      "W8BEN-Reference_numbers",
      "W8BEN-Treaty_country",
      "W8BEN-Treaty_article",
      "W8BEN-Treaty_withholding_rate",
      "W8BEN-Treaty_income_type",
      "W8BEN-Treaty_conditions_explanation",
      "GIIN",
      "Signature_date",
      "Signer_printed_name",
      "Signature_capacity",
      "Signature_unreadable",
      "isSigned",
      "isModified",
      "FormName",
    ];

    directMappings.forEach((field) => {
      if (responseModel[field] !== undefined) {
        formModel[field] = responseModel[field];
      }
    });

    // Special mappings
    this.mapSpecialFields(responseModel, formModel);

    // Handle dynamic fields from response
    Object.keys(responseModel).forEach((key) => {
      if (!directMappings.includes(key) && responseModel[key] !== undefined) {
        formModel[key] = responseModel[key];
      }
    });

    // Set mapping as done
    formModel["isMapDone"] = true;

    return formModel;
  }

  /**
   * Handle special field mappings and transformations
   */
  private static mapSpecialFields(
    responseModel: W8BENResponseModel,
    formModel: W8BENFormModel
  ): void {
    // Map API fields with underscores/dashes to form fields with spaces
    const apiToFormMappings: Record<string, string> = {
      First_name_and_middle_name: "First name and middle name",
      Last_name: "Last name",
      Date_of_birth: "Date of birth",
      Country_of_citizenship: "Country of citizenship",
      Permanent_address_line_1: "Permanent residence address",
      Permanent_address_line_2: "Permanent address line 2",
      Permanent_address_line_3: "Permanent address line 3",
      Permanent_address_city: "Permanent address city",
      Permanent_address_state_USCA: "Permanent address state USCA",
      Permanent_address_state_NonUS: "Permanent address state NonUS",
      Permanent_address_zip: "Permanent address zip",
      Permanent_address_country: "Permanent address country",
      Permanent_address_is_care_of: "Permanent address is care of",
      Permanent_address_is_po_box: "Permanent address is po box",
      Permanent_address_is_rural_route: "Permanent address is rural route",
      Mailing_address_line_1: "Mailing address",
      Mailing_address_line_2: "Mailing address line 2",
      Mailing_address_line_3: "Mailing address line 3",
      Mailing_city_address: "Mailing city address",
      Mailing_address_state_USCA: "Mailing address state USCA",
      Mailing_address_state_NonUS: "Mailing address state NonUS",
      Mailing_address_zip: "Mailing address zip",
      Mailing_address_country: "Mailing address country",
      Mailing_address_is_care_of: "Mailing address is care of",
      Mailing_address_is_po_box: "Mailing address is po box",
      Mailing_address_is_rural_route: "Mailing address is rural route",
      Taxpayer_email: "Taxpayer email",
      Taxpayer_type: "Taxpayer type",
      "U.S._taxpayer_identification_number": "U.S. taxpayer identification number",
      Foreign_tax_identifying_number: "Foreign tax identifying number",
      "W8BEN-FTIN_not_required": "W8BEN-FTIN_not_required",
      "W8BEN-Reference_numbers": "W8BEN-Reference_numbers",
      Signature_capacity: "Signature_capacity",
      Signer_printed_name: "Signer_printed_name",
      Signature_date: "Signature_date",
      Signature_unreadable: "Signature_unreadable",
    };

    // Apply API to form field mappings
    Object.entries(apiToFormMappings).forEach(([apiField, formField]) => {
      if (responseModel[apiField] !== undefined) {
        formModel[formField] = responseModel[apiField];
      }
    });

    // Split beneficial owner name into first and last name
    const beneficialOwnerName =
      responseModel["Beneficial Owner name"] || responseModel["Beneficial_Owner_name"];
    if (beneficialOwnerName && typeof beneficialOwnerName === "string") {
      const nameParts = beneficialOwnerName.split(" ");
      formModel["First name and middle name"] = nameParts[0] || "";
      formModel["Last name"] = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
    }

    // Map legacy address fields
    if (
      responseModel["Permanent residence address"] ||
      responseModel["Permanent_residence_address"]
    ) {
      const address =
        responseModel["Permanent residence address"] ||
        responseModel["Permanent_residence_address"];
      formModel["Permanent residence address"] = address;
    }

    if (responseModel["Permanent city address"] || responseModel["Permanent_city_address"]) {
      const cityAddress =
        responseModel["Permanent city address"] || responseModel["Permanent_city_address"];
      const addressComponents = this.parseAddressComponents(cityAddress);
      formModel["Permanent address city"] = addressComponents.city;
      const stateFieldType = determineStateFieldType(
        responseModel["Permanent address country"],
        addressComponents.state
      );

      if (stateFieldType === "USCA") {
        formModel["Permanent address state USCA"] = addressComponents.state;
        formModel["Permanent address state NonUS"] = "";
      } else {
        formModel["Permanent address state NonUS"] = addressComponents.state;
        formModel["Permanent address state USCA"] = "";
      }
      formModel["Permanent address zip"] = addressComponents.postalCode;
    }

    if (responseModel["Mailing address"] || responseModel["Mailing_address"]) {
      const address = responseModel["Mailing address"] || responseModel["Mailing_address"];
      formModel["Mailing address"] = address;
    }

    if (responseModel["Mailing address city"] || responseModel["Mailing_address_city"]) {
      const cityAddress =
        responseModel["Mailing address city"] || responseModel["Mailing_address_city"];
      const addressComponents = this.parseAddressComponents(cityAddress);
      formModel["Mailing city address"] = addressComponents.city;
      const stateFieldType = determineStateFieldType(
        responseModel["Mailing address country"],
        addressComponents.state
      );

      if (stateFieldType === "USCA") {
        formModel["Mailing address state USCA"] = addressComponents.state;
        formModel["Mailing address state NonUS"] = "";
      } else {
        formModel["Mailing address state NonUS"] = addressComponents.state;
        formModel["Mailing address state USCA"] = "";
      }

      formModel["Mailing address zip"] = addressComponents.postalCode;
    }

    // Copy signature fields and auto-set isSigned
    if (responseModel["Signature"]) {
      formModel["Signature"] = responseModel["Signature"];
    }

    // Auto-set isSigned based on Signature field
    if (responseModel["Signature"] && responseModel["Signature"].toString().trim()) {
      formModel["isSigned"] = "yes";
    } else {
      formModel["isSigned"] = "no";
    }
  }

  /**
   * Parse address components from a combined address string
   */
  private static parseAddressComponents(address: string): {
    city: string;
    state: string;
    postalCode: string;
  } {
    const result = { city: "", state: "", postalCode: "" };

    if (!address) return result;

    // Enhanced parsing to properly identify city, state, and postal code
    const parts = address.split(",").map((part) => part.trim());

    if (parts.length === 1) {
      // Only one part - assume it's the city
      result.city = parts[0];
    } else if (parts.length === 2) {
      // Two parts - check if second part is a postal code or state
      result.city = parts[0];
      const secondPart = parts[1];

      // Check if it's a postal code:
      // - Pure numeric (e.g., "456123", "12345")
      // - UK/Canada format with letters and numbers (e.g., "SE1 1SE", "M5H 2N2")
      // - Contains digits and is relatively short (typically < 15 chars)
      const hasDigits = /\d/.test(secondPart);
      const isShort = secondPart.length <= 15;
      const isPureNumeric = /^[\d\s\-]+$/.test(secondPart);
      const isAlphanumericPostal = /^[A-Z0-9\s\-]+$/i.test(secondPart) && hasDigits;

      if (isPureNumeric || (isAlphanumericPostal && isShort)) {
        result.postalCode = secondPart;
      } else {
        result.state = secondPart;
      }
    } else if (parts.length >= 3) {
      // Three or more parts - city, state, postal code
      result.city = parts[0];
      result.state = parts[1];
      result.postalCode = parts[2];
    }

    console.log(
      `🔍 W8BEN Address parsing: "${address}" → city: "${result.city}", state: "${result.state}", postalCode: "${result.postalCode}"`
    );

    return result;
  }
}
